import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { until } from "lit/directives/until.js";
import { SearchEvent, Api } from "../../api.js";
import { BaseElement } from "../../app.js";
import { userCache } from "./user-cache.js";

@customElement("search-events")
export class SearchEvents extends BaseElement {
    @property({ type: String })
    userId?: string;

    @state()
    private events: SearchEvent[] = [];

    @state()
    private loading = true;

    @state()
    private loadingMore = false;

    @state()
    private error = "";

    @state()
    private hasMore = true;

    @state()
    private currentPage = 0;

    private readonly pageSize = 20;
    private loadMoreRef = createRef<HTMLDivElement>();
    private observer?: IntersectionObserver;

    async firstUpdated() {
        await this.loadEvents();

        // Set up infinite scroll
        this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && this.hasMore && !this.loading && !this.loadingMore) {
                this.loadEvents(true);
            }
        });

        if (this.loadMoreRef.value) {
            this.observer.observe(this.loadMoreRef.value);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.observer?.disconnect();
    }

    private async loadEvents(loadMore = false) {
        if (loadMore) {
            this.loadingMore = true;
        } else {
            this.loading = true;
            this.currentPage = 0;
            this.events = [];
        }

        try {
            const result = await Api.getSearchEvents({
                page: this.currentPage,
                pageSize: this.pageSize,
                userId: this.userId,
            });

            if (loadMore) {
                this.events = [...this.events, ...result.events];
            } else {
                this.events = result.events;
            }

            this.hasMore = (this.currentPage + 1) * this.pageSize < result.total;
            if (this.hasMore) this.currentPage++;
        } catch (e) {
            this.error = "Failed to load search events";
        } finally {
            this.loading = false;
            this.loadingMore = false;
        }
    }

    render() {
        if (this.loading) {
            return html`<div class="p-4">Loading...</div>`;
        }

        if (this.error) {
            return html`<div class="p-4 text-red-600">${this.error}</div>`;
        }

        return html`
            <div class="bg-white rounded-lg shadow">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                                ${!this.userId
                                    ? html` <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th> `
                                    : nothing}
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Query</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paths</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Range</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            ${this.events.map(
                                (event) => html`
                                    <tr class="hover:bg-gray-50">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">${new Date(event.timestamp).toLocaleString()}</td>
                                        ${!this.userId
                                            ? html`
                                                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                                                      <a
                                                          href="/admin/users/${event.user_id}"
                                                          class="text-blue-600 hover:text-blue-900"
                                                          @mouseover=${() => userCache.getUserName(event.user_id)}
                                                      >
                                                          ${until(userCache.getUserName(event.user_id), event.user_id.slice(0, 7) + "...")}
                                                      </a>
                                                  </td>
                                              `
                                            : nothing}
                                        <td class="px-6 py-4 text-sm">${event.query || "-"}</td>
                                        <td class="px-6 py-4 text-sm">${event.paths?.join(", ") || "-"}</td>
                                        <td class="px-6 py-4 text-sm">
                                            ${event.date_from ? new Date(event.date_from).toLocaleDateString() : "-"}
                                            ${event.date_from || event.date_to ? "to" : ""}
                                            ${event.date_to ? new Date(event.date_to).toLocaleDateString() : ""}
                                        </td>
                                    </tr>
                                `
                            )}
                        </tbody>
                    </table>
                </div>
                ${this.hasMore
                    ? html`
                          <div ${ref(this.loadMoreRef)} class="p-4 flex justify-center">
                              ${this.loadingMore ? html`<div class="text-gray-500">Loading...</div>` : html`<div class="h-8"></div>`}
                          </div>
                      `
                    : null}
            </div>
        `;
    }
}
